import styles from "./Tutorial.module.css";
import { Button as GetiButton } from "@geti/identidade-visual-react";
import { useEffect, useState } from "react";
import { useSetState } from 'react-use';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { BsMortarboard } from 'react-icons/bs';
import tutorialStyles from "./TutorialStyles";
import {useLocation, useNavigate} from "react-router-dom";
import { logGroup } from "../Helper";
import { isLoggedIn } from "../../Auth/auth";

interface Props {
    breakpoint: string;
  }
  
interface State {
  run: boolean;
  steps: Step[];
}
const simulateClick = (selector: string) => {
  const element = document.querySelector(selector) as HTMLButtonElement | null;
  if (element) {
    element.click();
};}

const validateSelection = () => {
  const selection = window.getSelection();
  if (!selection || selection.toString().trim() === '') {
    return false;
  }
  return true;
};

export default function Tutorial(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isResponsive, setIsResponsive] = useState(false);
    const [{ run, steps }, setState] = useSetState<State>({
        run: false,
        steps: []
    }
    );

  useEffect(() => {
    const handleResize = () => {
      setIsResponsive(window.innerWidth >= 769);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleJoyrideCallback = (data: CallBackProps) => {
      let { action, index, status, type } = data;

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
          setState({ run: false });
          return;
      }
      const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

      if (location.pathname === "/") {
        if (index === 11)
          simulateClick('#revokeIcon');
        if((index === 11 && action==="prev") || action==="stop")
          simulateClick('#closeEditMenu')
      };

      if (location.pathname.startsWith('/revogar-ato')){
        if ( index === 4){
          if (status === STATUS.RUNNING) {
          const isValidSelection = validateSelection();
          if (!isValidSelection) {
            return;
          }
        }}
        if (action ===  "stop"){
          simulateClick("#cancelar")
        }
      }

      if (finishedStatuses.includes(status)) {
          setState({ run: false });
      };

      logGroup(type, data);
  };

  const handleClickStart = (event: React.MouseEvent<HTMLElement>) => {
      if (location.pathname === "/") {
        event.preventDefault();

        const firstPart: Step[] = [
          {
            content: <h3>Bem-vindo ao ATOS!</h3>,
            locale: { skip: <strong aria-label="skip">Cancelar</strong> },
            target: '#regulationContainer',
            disableBeacon: true,
          },
          {
            content: <h3>Selecione filtros para refinar sua busca.</h3>,
            target : '#searchContainer',
            disableBeacon: true,
          },
          {
            content: <h3>Escolha uma data inicial para buscar atos de um dia específico. Para um intervalo de tempo, defina também uma data final.</h3>,
            target : '#calendarsContainer',
            disableBeacon: true,
          },
          {
            content: <h3>Se nenhum filtro for aplicado, serão exibidos os atos mais recentes.</h3>,
            target : '#mainTable',
            disableBeacon: true,
          },
        ];

        const secondPartLogged: Step[] = [
          { title: "Coloque nos destaques",
            content: <h3>Adicione uma descrição ao ato clicando no botão correspondente.</h3>,
            target : '#pinButton',
            disableBeacon: true,
          },
          {
            content: <h3>Reordene-os arrastando os cartões dentro da tabela.</h3>,
            target : '#favoriteTableBody',
            disableBeacon: true,
          },
          {
            content: <h3>Remova o ato da tabela de destaques, se necessário.</h3>,
            target : '#removePinned',
            disableBeacon: true,
          },
          {
            content: <h3>Edite a descrição do ato existente.</h3>,
            target : '#editDescriptionButton',
            disableBeacon: true,
          },
        ];

        const thirdPart: Step[] = [
          {
            content: <h3>Visualize em uma nova página clicando no ícone correspondente.</h3>,
            target : '#view',
            disableBeacon: true,
          },
          {
            content: <h3>Baixe o arquivo para seu dispositivo com um clique.</h3>,
            target : '#download',
            disableBeacon: true,
          },
        ];

        const fourthPartLogged: Step[] = [
          {
            content: <h3>Exclua, se necessário.</h3>,
            target : '#delete',
            disableBeacon: true,
          },
          {
            content: <h3>Edite o ato publicado.</h3>,
            target : '#revokeIcon',
            disableBeacon: true,
            event: 'click',
          },
          {
            content: <h3>Lembre-se: somente nesta página é possível incluir, modificar ou revogar um ato. Na tela agendar, você apenas o envia.</h3>,
            target : '.MuiPaper-root',
            disableBeacon: true,
            disableScrollParentFix: false,
          },
          {
            content: <h3>Adicione novas informações ou formate o conteúdo do ato.</h3>,
            target : '#includeButton',
            disableBeacon: true,
            disableScrollParentFix: false,
          },
          {
            content: <h3>Altere parcialmente um ato, sempre referenciando o documento que realizou a modificação.</h3>,
            target : '#modifyButton',
            disableBeacon: true,
            disableScrollParentFix: false,
          },
          {
            content: <h3>Revogue o ato, indicando o documento substituto.</h3>,
            target : '#revokeButton',
            disableBeacon: true,
            disableScrollParentFix: false,
          },
        ];
        

        let steps: Step[] = [...firstPart, ...thirdPart];
        if (isLoggedIn()){
          steps = [
            ...firstPart,
            ...secondPartLogged,
            ...thirdPart,
            ...fourthPartLogged,
          ]
        }

        setState({
          run: true,
          steps
        });
      };

      if (location.pathname === '/agendar'){
        event.preventDefault();
        setState({
          run: true,
          steps:[
            {
              content: <h3>Aqui você pode agendar novos atos!</h3>,
              locale: { skip: <strong aria-label="skip">Cancelar</strong> },
              target: '#containerStatus',
              disableBeacon: true,
            },
            {
              content: <h3>Faça o upload do documento no formato .docx.</h3>,
              target : '#fileButton',
              disableBeacon: true,
            },
            {
              content: <h3>Escolha a data desejada para a publicação. Se nenhuma data for selecionada, o ato será publicado hoje, em alguns minutos.</h3>,
              target : '#scheduleButton',
              disableBeacon: true,
            },
            {
              content: <h3>Informe o número do ato <br/>
              Exemplo: para "Resolução nº 007", informe o número 007.</h3>,
              target : '#numberButton',
              disableBeacon: true,
            },
            {
              content: <h3>Opcional: Adicione uma breve descrição explicando sobre o ato.</h3>,
              target : '#titleButton',
              disableBeacon: true,
            },
            {
              content: <h3>Opcional: Defina um horário específico. Se não definido, o ato será publicado automaticamente na data escolhida.</h3>,
              target : '#timeButton',
              disableBeacon: true,
            },
            {
              content: <h3>Opcional: Adicione uma URL. Caso queira vincular a publicação do Diário Oficial, insira o link correspondente.</h3>,
              target : '#urlButton',
              disableBeacon: true,
            },
            {
              content: <h3>Clique em "Agendar" para concluir o processo.</h3>,
              target : '#submitButton',
              disableBeacon: true,
            },
            {
              content: <h3>Se o ato alterar ou revogar outro ato <br/>
              Após o agendamento, vá para a página inicial.
              </h3>,              
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
            {
              content: <h3>
              Encontre o ato que será alterado ou revogado.<br/>
              Clique em "Editar" e selecione a opção correspondente.
              </h3>,              
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
          ]
        })
      };

      if (location.pathname.startsWith('/incluir')){
        event.preventDefault();
        setState({
          run: true,
          steps:[
            {
              content: <h3>Edite o texto aqui!</h3>,
              locale: { skip: <strong aria-label="skip">Cancelar</strong> },
              target : '#textEditor',
              disableBeacon: true,
            },
            {
              title: "Utilize as ferramentas de edição",
              content: <h3>No cabeçalho, você encontrará ferramentas para formatar e editar o texto.</h3>,
              target : '.se-btn-tray',
              disableBeacon: true,
            },            
            {
              title: "Incluir um novo texto no ato",
              content: <h3>Sempre utilize a seguinte mensagem ao lado do novo texto adicionado:<br/>
                "INCLUÍDO POR: XXX Nº XXX DE XX/XX/XXXX".
                </h3>,
              target : 'body',
              placement: 'center',
              disableBeacon: true,
            },
            {
              content: <h3>Para facilitar, você pode copiar essa mensagem clicando aqui!</h3>,
              target : '#copyButton',
              disableBeacon: true,
            },
            {
              title: "Salvar a formatação",
              content: <h3>Use o botão "Formatar" para garantir que todas as alterações no estilo do texto sejam aplicadas corretamente.</h3>,
              target : '#formatButton',
              disableBeacon: true,
            },
            {
              title: "Finalizar e adicionar o texto ao ato",
              content: <h3>Após revisar tudo, clique aqui para vincular o novo texto ao ato.</h3>,
              target : '#linkButton',
              disableBeacon: true,
            },
          ]
        })
      }

      if (location.pathname.startsWith('/revogar-ato')){
        event.preventDefault();
        setState({
          run: true,
          steps:[
            {
              content: <h3>Modificando ou Revogando Parcialmente um Ato</h3>,
              locale: { skip: <strong aria-label="skip">Cancelar</strong> },
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
            {
              title: "Selecione o trecho do ato",
              content: <h3>Escolha a parte do texto que será modificada ou revogada.</h3>,
              locale: { skip: <strong aria-label="skip">Cancelar</strong> },
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
            {
              content: <h3>Para múltiplas alterações, selecione um trecho por vez.</h3>,
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
            {
              content: <h3>Cada trecho pode ser alterado por um ato modificador diferente, mas ele precisa estar publicado antes da modificação.</h3>,
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
            {
              content: <h3>Caso seu ato seja uma tabela, selecione apenas uma linha ou a tabela inteira</h3>,
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
            {
              content: <h3>Selecione um trecho como exemplo para visualizar o processo. </h3>,
              placement: 'bottom',
              target: 'main',
              disableBeacon: true,
              disableOverlay: true,
            },
            {
              content: <h3>Digite o novo texto que substituirá o trecho selecionado aqui. </h3>,
              target : '#para',
              disableBeacon: true,
              disableScrollParentFix: false,
            },
            {
              content: <h3>Se for a linha de uma tabela, coloque duas barras "//" para indicar o conteúdo de cada célula </h3>,
              target : '#para',
              disableBeacon: true,
              disableScrollParentFix: false,
            },
            {
              content: <h3>Exemplo:<br/> Defensoria Pública // Santa Catarina // Brasil // 2025</h3>,
              target : '#para',
              disableBeacon: true,
              disableScrollParentFix: false,
            },
            {
              content: <h3>Essa seleção é opcional</h3>,
              target : '#checksContainer',
              disableBeacon: true,
            },
            {
              content: <h3>Selecione aqui, o trecho será riscado e marcado como "Revogado por:"</h3>,
              target : '#revokeCheck',
              disableBeacon: true,
            },
            {
              content: <h3>Aqui o trecho também será riscado e marcado como "Execução suspensa por:"</h3>,
              target : '#suspendedExecutionCheck',
              disableBeacon: true,
            },
            {
              content: <h3>Neste, o trecho <b>não</b> é riscado e marcado como "Vide:"</h3>,
              target : '#videCheck',
              disableBeacon: true,
            },
            {
              title: "Associe o ato modificador",
              content: <h3>Escolha o ato que modifica o trecho selecionado. </h3>,
              target : '.MuiAutocomplete-hasPopupIcon',
              disableBeacon: true,
              disableScrollParentFix: false,
            },
          ]
        })
      };
      if (location.pathname === ('/historico')){
        event.preventDefault();
        setState({
          run: true,
          steps:[
            {
              content: <h3>Esta página mostra o registro de ações realizadas nos ATOS</h3>,
              locale: { skip: <strong aria-label="skip">Cancelar</strong> },
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
            {
              title: "Filtro de pesquisa",
              content: <h3>Escolha as caracteristicas do registro que você está buscando.</h3>,
              locale: { skip: <strong aria-label="skip">Cancelar</strong> },
              target: '#searchContainer',
              disableBeacon: true,
            },
            {
              content: <h3>Aqui é onde está todos os registros do histórico.</h3>,
              target: '#mainTable',
              disableBeacon: true,
            },
            {
              content: <h3>Esta é a legenda sobre as cores de cada registro.</h3>,
              target: '#tableLegend',
              disableBeacon: true,
            },
            {
              content: <h3>Caso tenha alguma dúvida, abra um chamado no SIT</h3>,
              locale: { skip: <strong aria-label="skip">Cancelar</strong> },
              placement: 'center',
              target: 'body',
              disableBeacon: true,
            },
          ]
        })
      };
    }
  return (
    (location.pathname === "/" || "/agendar" || "/incluir" || "/revogar-ato") && (
      <GetiButton onClick={handleClickStart} className={`${styles.tutorial} ${styles.button}`}>
        <BsMortarboard className={styles.button_tutorial} />
          <Joyride
              callback={handleJoyrideCallback}
              continuous
              run={run}
              scrollToFirstStep
              showProgress
              showSkipButton
              steps={steps}
              styles={tutorialStyles}
              disableScrollParentFix={true}
              locale={{
                  back: 'Voltar',
                  close: 'Fechar', 
                  last: 'Finalizar', 
                  next: 'Próximo', 
                  nextLabelWithProgress: 'Próximo ({step} de {steps})', 
                  open: 'Abrir tutorial', 
                  skip: 'Cancelar'
              }}
          />
      </GetiButton>
  )
  || null
  );  
}